import React, { useCallback, useEffect, useState } from 'react';
import { errorMessage } from '../admin/adminApi';
import { useQueryParams } from '../admin/useQueryParams';
import { formatDate } from '../admin/format';
import {
  Badge, DataTable, Drawer, ListLayout, Panel, SearchInput, Spinner, Toolbar,
  btnPrimary, btnSecondary, inputClass, labelClass
} from '../admin/AdminUI';
import { formatAmount } from '../approvals/approvalTypes';
import { downloadFile } from '../documents/documentUtils';

const TEXT_FIELDS = [
  ['name', 'Name *'], ['trade', 'Trade'], ['contactName', 'Contact'], ['email', 'Email'], ['phone', 'Phone'], ['gstNumber', 'GST number']
];
const DATE_FIELDS = [['insuranceExpiresAt', 'Insurance expires'], ['worksafeExpiresAt', 'WorkSafeBC clearance expires'], ['contractEndsAt', 'Contract ends']];
const WARNING_CLASSES = { bad: 'text-destructive border-destructive normal-case', warn: 'text-warning border-warning normal-case' };

const day = (value) => (value ? value.slice(0, 10) : '');

function VendorForm({ vendor, people, readOnly, onSubmit, onCancel }) {
  const [form, setForm] = useState(() => ({
    ...Object.fromEntries(TEXT_FIELDS.map(([key]) => [key, vendor?.[key] || ''])),
    ...Object.fromEntries(DATE_FIELDS.map(([key]) => [key, day(vendor?.[key])])),
    address: vendor?.address || '',
    contractNotes: vendor?.contractNotes || '',
    notes: vendor?.notes || '',
    active: vendor ? vendor.active !== false : true,
    conflictUserIds: vendor?.conflictUserIds || []
  }));
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = { ...form };
      DATE_FIELDS.forEach(([key]) => { body[key] = form[key] || null; });
      if (!people) delete body.conflictUserIds;
      await onSubmit(body);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      <fieldset disabled={readOnly} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          {TEXT_FIELDS.map(([key, label]) => (
            <div key={key}>
              <label htmlFor={`vendor-${key}`} className={labelClass}>{label}</label>
              <input id={`vendor-${key}`} required={key === 'name'} value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} className={inputClass} />
            </div>
          ))}
          {DATE_FIELDS.map(([key, label]) => (
            <div key={key}>
              <label htmlFor={`vendor-${key}`} className={labelClass}>{label}</label>
              <input id={`vendor-${key}`} type="date" value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} className={inputClass} />
            </div>
          ))}
        </div>
        {[['address', 'Address'], ['contractNotes', 'Contract'], ['notes', 'Notes']].map(([key, label]) => (
          <div key={key}>
            <label htmlFor={`vendor-${key}`} className={labelClass}>{label}</label>
            <textarea id={`vendor-${key}`} rows={2} value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} className={inputClass} />
          </div>
        ))}
        {people && (
          <div>
            <label htmlFor="vendor-conflicts" className={labelClass}>People with a conflict of interest</label>
            <select
              id="vendor-conflicts"
              multiple
              value={form.conflictUserIds}
              onChange={(e) => setForm({ ...form, conflictUserIds: Array.from(e.target.selectedOptions).map((o) => o.value) })}
              className={`${inputClass} h-32`}
            >
              {people.map((p) => <option key={p._id} value={p._id}>{p.name}</option>)}
            </select>
            <p className="text-xs text-muted-foreground mt-1">Their invoices skip them: they can't approve, sign or pay them (Strata Property Act s.32).</p>
          </div>
        )}
        <label className="flex items-center gap-2 text-sm text-foreground">
          <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
          Active vendor
        </label>
      </fieldset>
      {!readOnly && (
        <div className="flex gap-2">
          <button type="submit" disabled={saving} className={btnPrimary}>{saving ? 'Saving...' : vendor ? 'Save vendor' : 'Add vendor'}</button>
          <button type="button" onClick={onCancel} className={btnSecondary}>Cancel</button>
        </div>
      )}
    </form>
  );
}

function VendorQuotes({ api, quotesBase, vendor, canEdit, practice }) {
  const [quotes, setQuotes] = useState(null);
  const [form, setForm] = useState({ title: '', amount: '', validUntil: '' });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(quotesBase, { vendor: vendor._id }).then((r) => setQuotes(r.data.quotes)).catch(() => setQuotes([]));
  }, [api, quotesBase, vendor._id]);

  useEffect(() => { load(); }, [load]);

  const run = async (request) => {
    setBusy(true);
    try {
      await request();
      load();
      return true;
    } catch (err) {
      alert(errorMessage(err, 'That did not work'));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const upload = async (e) => {
    e.preventDefault();
    const body = new FormData();
    body.append('file', file);
    body.append('vendorId', vendor._id);
    Object.entries(form).forEach(([key, value]) => body.append(key, value));
    if (await run(() => api.post(quotesBase, body))) setForm({ title: '', amount: '', validUntil: '' });
  };

  return (
    <section className="mt-6 pt-4 border-t border-border">
      <h3 className="text-sm font-semibold text-foreground mb-2">Quotes</h3>
      {!quotes ? <Spinner /> : quotes.length === 0 ? <p className="text-sm text-muted-foreground">No quotes yet.</p> : (
        <ul className="space-y-2">
          {quotes.map((q) => (
            <li key={q._id} className="p-2 rounded border border-border text-sm">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium text-foreground">{q.title}</span>
                <span className="text-foreground">{formatAmount(q.amount)}</span>
                <Badge className="normal-case">{q.status}</Badge>
                {q.test && <Badge className="text-warning border-warning">Test</Badge>}
                <button type="button" onClick={() => downloadFile(() => api.download(`${quotesBase}/${q._id}/file`), q.file.name).catch(() => alert('Failed to download the quote'))} className="ml-auto text-primary text-sm">Download</button>
              </div>
              <p className="text-xs text-muted-foreground">Received {formatDate(q.receivedAt)}{q.validUntil ? ` · valid until ${formatDate(q.validUntil)}` : ''} · added by {q.addedFrom}</p>
              {canEdit && (!practice || q.test) && q.status === 'received' && (
                <div className="flex gap-2 mt-2">
                  <button type="button" disabled={busy} onClick={() => run(() => api.put(`${quotesBase}/${q._id}`, { status: 'accepted' }))} className={btnSecondary}>Accept</button>
                  <button type="button" disabled={busy} onClick={() => run(() => api.put(`${quotesBase}/${q._id}`, { status: 'declined' }))} className={btnSecondary}>Decline</button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      {canEdit && (
        <form onSubmit={upload} className="mt-3 space-y-2">
          <label htmlFor="quote-file" className={labelClass}>Add a quote</label>
          <input id="quote-file" type="file" required onChange={(e) => setFile(e.target.files[0] || null)} className="text-sm text-foreground" />
          <div className="grid grid-cols-3 gap-2">
            <input aria-label="Quote title" required placeholder="What it's for" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputClass} />
            <input aria-label="Quote amount" required type="number" min="0" step="0.01" placeholder="Amount" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className={inputClass} />
            <input aria-label="Valid until" type="date" value={form.validUntil} onChange={(e) => setForm({ ...form, validUntil: e.target.value })} className={inputClass} />
          </div>
          <button type="submit" disabled={busy} className={btnSecondary}>Add quote</button>
        </form>
      )}
    </section>
  );
}

// The vendor directory and quotes, for the Admin and council (context/SelfManagement.md). `people`
// (Admin only) lets the Admin record conflicts of interest; `practice` makes real records read-only.
function VendorsLibrary({ api, base, quotesBase, canEdit = true, practice = false, people = null, notify = () => {} }) {
  const [params, setParams] = useQueryParams();
  const [vendors, setVendors] = useState(null);
  const q = (params.get('q') || '').toLowerCase();
  const selectedId = params.get('vendor');
  const creating = params.get('new') === '1';

  const load = useCallback(async () => {
    try {
      setVendors((await api.get(base)).data.vendors);
    } catch (err) {
      alert(errorMessage(err, 'Failed to load vendors'));
    }
  }, [api, base]);

  useEffect(() => { load(); }, [load]);

  const save = async (body, vendor) => {
    try {
      const response = vendor ? await api.put(`${base}/${vendor._id}`, body) : await api.post(base, body);
      notify(vendor ? 'Vendor saved.' : 'Vendor added.');
      setParams({ new: null, vendor: response.data.vendor._id });
      load();
    } catch (err) {
      alert(errorMessage(err, 'Failed to save the vendor'));
    }
  };

  const rows = (vendors || []).filter((v) => !q || [v.name, v.trade, v.contactName, v.email].some((t) => t && t.toLowerCase().includes(q)));
  const selected = (vendors || []).find((v) => v._id === selectedId);

  const columns = [
    { key: 'name', header: 'Vendor', render: (v) => <span className={`font-medium ${v.active ? 'text-foreground' : 'text-muted-foreground'}`}>{v.name}{!v.active && ' (inactive)'}</span> },
    { key: 'trade', header: 'Trade', hideBelow: 'md', className: 'text-muted-foreground', render: (v) => v.trade || '—' },
    { key: 'contact', header: 'Contact', hideBelow: 'lg', className: 'text-muted-foreground', render: (v) => [v.contactName, v.phone].filter(Boolean).join(' · ') || '—' },
    {
      key: 'warnings',
      header: 'Needs attention',
      render: (v) => (v.warnings.length === 0 ? <span className="text-success text-sm">✓</span> : (
        <div className="flex flex-wrap gap-1">{v.warnings.map((w, i) => <Badge key={i} className={WARNING_CLASSES[w.level]}>{w.text}</Badge>)}</div>
      ))
    }
  ];

  const drawer = creating ? (
    <Drawer title="Add a vendor" onClose={() => setParams({ new: null })}>
      {canEdit ? <VendorForm people={people} onSubmit={(body) => save(body)} onCancel={() => setParams({ new: null })} />
        : <p className="text-sm text-muted-foreground">Pick who you are acting as first.</p>}
    </Drawer>
  ) : selected ? (
    <Drawer title={selected.name} badges={selected.test ? <Badge className="text-warning border-warning">Test</Badge> : null} subtitle={selected.trade} onClose={() => setParams({ vendor: null })}>
      <VendorForm key={selected._id} vendor={selected} people={people} readOnly={!canEdit || (practice && !selected.test)} onSubmit={(body) => save(body, selected)} onCancel={() => setParams({ vendor: null })} />
      <VendorQuotes api={api} quotesBase={quotesBase} vendor={selected} canEdit={canEdit} practice={practice} />
    </Drawer>
  ) : null;

  return (
    <ListLayout drawer={drawer}>
      <Panel>
        <Toolbar>
          <h2 className="text-base font-semibold text-foreground mr-2">Vendors{vendors ? ` (${vendors.length})` : ''}</h2>
          <SearchInput value={params.get('q') || ''} onChange={(value) => setParams({ q: value }, { replace: true })} placeholder="Search vendors..." />
          <button type="button" onClick={() => setParams({ new: '1', vendor: null })} className={btnPrimary}>+ Add vendor</button>
        </Toolbar>
        {!vendors ? <Spinner /> : (
          <DataTable label="Vendors" columns={columns} rows={rows} selectedId={selectedId} onRowClick={(v) => setParams({ vendor: v._id, new: null })} emptyMessage={q ? 'No vendors match.' : 'No vendors yet.'} />
        )}
      </Panel>
    </ListLayout>
  );
}

export default VendorsLibrary;
